import { FC, useState, useCallback, useMemo, HTMLProps, Key, ChangeEvent, FocusEvent } from 'react';
import { cn, Input, CustomInputProps, Listbox, ListboxItem } from '@ds';
import { HereGeocodeItemType } from '@/api/apis/here-maps';

interface AddressSearchProps {
  addressList: HereGeocodeItemType[];
  onAddressSelect: (addressId: string) => void;
  onAddressInputChange: (value: string) => void;
  value: string;
  isDisabled?: boolean;
  onBlur?: () => void;
  onChange?: (value: string) => void;
  isInvalid?: boolean;
  errorMessage?: string;
  inputProps?: CustomInputProps;
  wrapperProps?: HTMLProps<HTMLDivElement>;
  listBoxWrapperProps?: HTMLProps<HTMLDivElement>;
}

export const AddressSearch: FC<AddressSearchProps> = ({
  addressList,
  onAddressSelect,
  onAddressInputChange,
  value,
  onBlur,
  onChange,
  isInvalid,
  isDisabled = false,
  errorMessage,
  inputProps,
  wrapperProps,
  listBoxWrapperProps,
}) => {
  const [listBoxVisible, setListBoxVisible] = useState(false);

  const handleInputFocus = useCallback(() => {
    setListBoxVisible(true);
  }, []);

  const handleInputBlur = useCallback(
    (e: FocusEvent) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        setTimeout(() => {
          setListBoxVisible(false);
          onBlur?.();
        }, 150);
      }
    },
    [onBlur],
  );

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange?.(newValue);
    onAddressInputChange(newValue);
    setListBoxVisible(true);
  };

  const handleListItemClick = useCallback(
    (key: Key) => {
      if (typeof key === 'string') {
        onAddressSelect(key);
        setListBoxVisible(false);
      }
    },
    [onAddressSelect],
  );

  const displayedAddressList = useMemo(() => {
    return value?.trim() ? addressList : [];
  }, [value, addressList]);

  return (
    <div {...wrapperProps} className={`relative ${wrapperProps?.className || ''}`}>
      <Input
        value={value}
        onFocus={handleInputFocus}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        isInvalid={isInvalid}
        isDisabled={isDisabled}
        errorMessage={errorMessage}
        {...inputProps}
      />
      {!isDisabled && (
        <div
          className={`absolute z-11 w-full mt-1 subpixel-antialiased outline-none box-border text-small w-full bg-base-60 shadow-xl rounded-lg overflow-y-auto max-h-58 ${listBoxWrapperProps?.className ? listBoxWrapperProps.className : ''}`}
        >
          <Listbox
            aria-label="Address suggestions"
            variant="flat"
            disallowEmptySelection
            selectionMode="single"
            onAction={handleListItemClick}
            className={cn([
              'inline-flex flex-col items-center justify-center p-1 gap-0.5',
              { hidden: !listBoxVisible },
            ])}
          >
            {displayedAddressList.length > 0 ? (
              displayedAddressList.map((item) => (
                <ListboxItem
                  className="z-999999 flex group gap-2 items-center justify-between relative w-full h-full box-border subpixel-antialiased cursor-pointer tap-highlight-transparent outline-none data-[focus-visible=true]:z-10 data-[focus-visible=true]:outline-2 data-[focus-visible=true]:outline-focus data-[focus-visible=true]:outline-offset-2 data-[focus-visible=true]:dark:ring-offset-background-content1 data-[hover=true]:transition-colors data-[hover=true]:bg-default data-[hover=true]:text-default-foreground data-[selectable=true]:focus:bg-default data-[selectable=true]:focus:text-default-foreground px-3 py-2 *:text-m-r! rounded-lg *:text-base-5 data-[hover=true]:bg-base-50! data-[selected=true]:bg-base-50! [&[data-selected=true]_[data-hover=true]]:bg-base-50! [&[data-selected=true]_[aria-hidden=true]]:hidden"
                  key={item.id}
                  textValue={item.address.label}
                >
                  <span className="flex-1 font-m-r text-wrap">{item.address.label}</span>
                </ListboxItem>
              ))
            ) : (
              <ListboxItem
                className="z-999999 flex group gap-2 items-center justify-between relative w-full h-full box-border subpixel-antialiased cursor-default tap-highlight-transparent outline-none *:text-s-r! rounded-lg *:text-base-5 data-[hover=true]:bg-transparent"
                key="no-results"
                textValue="No results found"
              >
                <span className="flex-1 text-s-r truncate text-base-30">
                  {value?.trim() ? 'No results found.' : 'Type to search'}
                </span>
              </ListboxItem>
            )}
          </Listbox>
        </div>
      )}
    </div>
  );
};
